// v2.12.0 — OPS tab générique : modération des profils Santé et Justice.
// Réutilise le pattern OpsLiveEventsTab : table + actions inline.
// Props : kind = "health" | "justice"

import { useEffect, useState, useCallback, useMemo } from "react";
import axios from "axios";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL || ""}/api`;

const TIERS = ["free", "basic", "premium", "partner"];
const TIER_COLORS = {
  partner: "#A855F7",
  premium: "#F59E0B",
  basic: "#3B82F6",
  free: "#64748B",
};

const KIND_LABEL = {
  health: { title: "👨‍⚕️ Santé — Profils", catApi: "health" },
  justice: { title: "⚖️ Justice — Profils", catApi: "justice" },
};

export default function OpsProfilesTab({ token, kind }) {
  const [categories, setCategories] = useState([]);
  const [profiles, setProfiles] = useState([]);
  const [filters, setFilters] = useState({
    cat_id: "",
    verified: "",
    sponsored_tier: "",
    include_deleted: false,
  });
  const [loading, setLoading] = useState(false);
  const [busyId, setBusyId] = useState(null);

  const headers = useMemo(() => ({ "x-admin-token": token }), [token]);
  const meta = KIND_LABEL[kind] || KIND_LABEL.health;

  const fetchCategories = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/${meta.catApi}/categories`);
      setCategories(r.data.categories || []);
    } catch (_) {
      setCategories([]);
    }
  }, [meta.catApi]);

  const fetchProfiles = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filters.cat_id) params.set("cat_id", filters.cat_id);
      if (filters.verified !== "") params.set("verified", filters.verified);
      if (filters.sponsored_tier) params.set("sponsored_tier", filters.sponsored_tier);
      if (filters.include_deleted) params.set("include_deleted", "1");
      const r = await axios.get(`${API}/ops/${kind}/profiles?${params.toString()}`, { headers });
      setProfiles(r.data.profiles || []);
    } catch (e) {
      toast.error(e.response?.data?.error || `Erreur chargement ${kind}`);
      setProfiles([]);
    } finally {
      setLoading(false);
    }
  }, [kind, headers, filters]);

  useEffect(() => { fetchCategories(); }, [fetchCategories]);
  useEffect(() => { fetchProfiles(); }, [fetchProfiles]);

  const patchProfile = async (p, patch) => {
    setBusyId(p.id);
    try {
      await axios.patch(`${API}/ops/${kind}/profiles/${p.id}`, patch, { headers });
      toast.success(`✅ ${p.nom || p.id} mis à jour`);
      fetchProfiles();
    } catch (e) {
      toast.error(e.response?.data?.error || e.message);
    } finally {
      setBusyId(null);
    }
  };

  const onDelete = async (p) => {
    if (!window.confirm(`Soft-delete "${p.nom || p.id}" ?`)) return;
    setBusyId(p.id);
    try {
      await axios.delete(`${API}/ops/${kind}/profiles/${p.id}`, { headers });
      toast.success("Profil soft-deleted");
      fetchProfiles();
    } catch (e) {
      toast.error(e.response?.data?.error || e.message);
    } finally { setBusyId(null); }
  };

  const onRestore = async (p) => {
    setBusyId(p.id);
    try {
      await axios.post(`${API}/ops/${kind}/profiles/${p.id}/restore`, {}, { headers });
      toast.success("Profil restauré");
      fetchProfiles();
    } catch (e) {
      toast.error(e.response?.data?.error || e.message);
    } finally { setBusyId(null); }
  };

  // Counters
  const counters = useMemo(() => {
    const c = { total: profiles.length, verified: 0, partner: 0, premium: 0, basic: 0, free: 0, deleted: 0 };
    profiles.forEach((p) => {
      if (p.verified) c.verified += 1;
      if (p.deleted_at) c.deleted += 1;
      const t = p.sponsored_tier || "free";
      if (c[t] != null) c[t] += 1;
    });
    return c;
  }, [profiles]);

  return (
    <div data-testid={`ops-${kind}-tab`} style={{ padding: 16 }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 14, flexWrap: "wrap", gap: 8 }}>
        <h3 style={{ margin: 0 }}>{meta.title}</h3>
        <div style={{ fontSize: 12, opacity: 0.8, display: "flex", gap: 12, flexWrap: "wrap" }}>
          <span><strong>{counters.total}</strong> affichés</span>
          <span>✓ <strong>{counters.verified}</strong> vérifiés</span>
          <span style={{ color: TIER_COLORS.partner }}>● <strong>{counters.partner}</strong> partner</span>
          <span style={{ color: TIER_COLORS.premium }}>● <strong>{counters.premium}</strong> premium</span>
          <span style={{ color: TIER_COLORS.basic }}>● <strong>{counters.basic}</strong> basic</span>
          <span style={{ color: TIER_COLORS.free }}>● <strong>{counters.free}</strong> free</span>
          {counters.deleted > 0 && <span style={{ color: "#ef4444" }}>🗑️ {counters.deleted} deleted</span>}
        </div>
      </div>

      {/* Filters */}
      <div style={{
        display: "flex", gap: 10, flexWrap: "wrap", marginBottom: 12, padding: 10,
        background: "rgba(255,255,255,0.04)", borderRadius: 6,
      }}>
        <label style={{ fontSize: 11, color: "#94a3b8" }}>
          Catégorie
          <select
            value={filters.cat_id}
            onChange={(e) => setFilters({ ...filters, cat_id: e.target.value })}
            data-testid={`ops-${kind}-filter-cat`}
            style={{ display: "block", marginTop: 3, padding: 4, background: "rgba(255,255,255,0.06)", border: "1px solid rgba(255,255,255,0.12)", color: "#fff", borderRadius: 4, fontSize: 12, minWidth: 180 }}
          >
            <option value="" style={{ background: "#0f172a" }}>— Toutes ({categories.length}) —</option>
            {categories.map((c) => (
              <option key={c.id} value={c.id} style={{ background: "#0f172a" }}>
                {c.emoji} {c.label} ({c.profiles_count})
              </option>
            ))}
          </select>
        </label>

        <label style={{ fontSize: 11, color: "#94a3b8" }}>
          Vérifié
          <select
            value={filters.verified}
            onChange={(e) => setFilters({ ...filters, verified: e.target.value })}
            data-testid={`ops-${kind}-filter-verified`}
            style={{ display: "block", marginTop: 3, padding: 4, background: "rgba(255,255,255,0.06)", border: "1px solid rgba(255,255,255,0.12)", color: "#fff", borderRadius: 4, fontSize: 12 }}
          >
            <option value="" style={{ background: "#0f172a" }}>— Tous —</option>
            <option value="1" style={{ background: "#0f172a" }}>✓ Vérifiés</option>
            <option value="0" style={{ background: "#0f172a" }}>✗ Non vérifiés</option>
          </select>
        </label>

        <label style={{ fontSize: 11, color: "#94a3b8" }}>
          Tier
          <select
            value={filters.sponsored_tier}
            onChange={(e) => setFilters({ ...filters, sponsored_tier: e.target.value })}
            data-testid={`ops-${kind}-filter-tier`}
            style={{ display: "block", marginTop: 3, padding: 4, background: "rgba(255,255,255,0.06)", border: "1px solid rgba(255,255,255,0.12)", color: "#fff", borderRadius: 4, fontSize: 12 }}
          >
            <option value="" style={{ background: "#0f172a" }}>— Tous —</option>
            {TIERS.map((t) => (
              <option key={t} value={t} style={{ background: "#0f172a" }}>{t}</option>
            ))}
          </select>
        </label>

        <label style={{ fontSize: 12, display: "flex", alignItems: "flex-end", gap: 4, color: "#cbd5e1" }}>
          <input
            type="checkbox"
            checked={filters.include_deleted}
            onChange={(e) => setFilters({ ...filters, include_deleted: e.target.checked })}
            data-testid={`ops-${kind}-include-deleted`}
          />
          Inclure soft-deleted
        </label>

        <button
          onClick={fetchProfiles}
          data-testid={`ops-${kind}-refresh`}
          style={{ marginLeft: "auto", alignSelf: "flex-end", background: "rgba(255,255,255,0.08)", color: "#cbd5e1", border: "1px solid rgba(255,255,255,0.12)", padding: "6px 12px", borderRadius: 4, cursor: "pointer", fontSize: 12 }}
        >
          🔄 Refresh
        </button>
      </div>

      {/* Table */}
      <div style={{ overflowX: "auto" }}>
        <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }} data-testid={`ops-${kind}-table`}>
          <thead>
            <tr style={{ background: "rgba(255,255,255,0.04)", textAlign: "left" }}>
              <th style={{ padding: 8 }}>Nom</th>
              <th>Catégorie</th>
              <th>Adresse CIV</th>
              <th>Téléphone</th>
              <th>Vérifié</th>
              <th>Tier</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr><td colSpan={7} style={{ padding: 16, textAlign: "center", opacity: 0.6 }}>Chargement…</td></tr>
            )}
            {!loading && profiles.length === 0 && (
              <tr><td colSpan={7} style={{ padding: 16, textAlign: "center", opacity: 0.6 }}>Aucun profil avec ces filtres.</td></tr>
            )}
            {!loading && profiles.map((p) => {
              const busy = busyId === p.id;
              const tier = p.sponsored_tier || "free";
              return (
                <tr
                  key={p.id}
                  data-testid={`ops-${kind}-row-${p.id}`}
                  style={{
                    borderBottom: "1px solid rgba(255,255,255,0.06)",
                    opacity: p.deleted_at ? 0.45 : 1,
                  }}
                >
                  <td style={{ padding: 8 }}>
                    <div style={{ fontWeight: 600 }}>{p.nom || p.id}</div>
                    {p.specialite && <div style={{ fontSize: 11, opacity: 0.65 }}>{p.specialite}</div>}
                    {p.categorie_etablissement && <div style={{ fontSize: 11, opacity: 0.65 }}>{p.categorie_etablissement}</div>}
                    {p.deleted_at && <span style={{ color: "#ef4444", fontSize: 11 }}>[deleted]</span>}
                  </td>
                  <td style={{ fontSize: 11, opacity: 0.8 }}>{p._category_id}</td>
                  <td style={{ fontFamily: "monospace", fontSize: 11 }}>{p.adresse_civ || "—"}</td>
                  <td style={{ fontSize: 12 }}>{p.telephone || "—"}</td>
                  <td style={{ textAlign: "center" }}>
                    <button
                      onClick={() => patchProfile(p, { verified: !p.verified })}
                      disabled={busy || !!p.deleted_at}
                      data-testid={`ops-${kind}-verify-${p.id}`}
                      title={p.verified ? "Cliquer pour dé-vérifier" : "Cliquer pour vérifier"}
                      style={{
                        background: p.verified ? "#16A34A" : "rgba(255,255,255,0.08)",
                        color: p.verified ? "#fff" : "#cbd5e1",
                        border: "1px solid " + (p.verified ? "#16A34A" : "rgba(255,255,255,0.12)"),
                        padding: "4px 10px", borderRadius: 4,
                        cursor: busy || p.deleted_at ? "not-allowed" : "pointer", fontSize: 11, fontWeight: 600,
                      }}
                    >
                      {p.verified ? "✓ Vérifié" : "Non vérifié"}
                    </button>
                  </td>
                  <td>
                    <select
                      value={tier}
                      disabled={busy || !!p.deleted_at}
                      onChange={(e) => patchProfile(p, { sponsored_tier: e.target.value })}
                      data-testid={`ops-${kind}-tier-${p.id}`}
                      style={{
                        background: "rgba(255,255,255,0.06)",
                        color: TIER_COLORS[tier] || "#fff",
                        border: `1px solid ${TIER_COLORS[tier] || "rgba(255,255,255,0.12)"}`,
                        borderRadius: 4, padding: "3px 6px", fontSize: 12, fontWeight: 600,
                        cursor: busy || p.deleted_at ? "not-allowed" : "pointer",
                      }}
                    >
                      {TIERS.map((t) => (
                        <option key={t} value={t} style={{ background: "#0f172a", color: "#fff" }}>{t}</option>
                      ))}
                    </select>
                  </td>
                  <td style={{ display: "flex", gap: 4 }}>
                    {p.deleted_at ? (
                      <button
                        onClick={() => onRestore(p)}
                        disabled={busy}
                        data-testid={`ops-${kind}-restore-${p.id}`}
                        style={{ background: "#16A34A", color: "#fff", border: "none", padding: "4px 8px", borderRadius: 4, cursor: busy ? "wait" : "pointer", fontSize: 11 }}
                      >
                        Restore
                      </button>
                    ) : (
                      <button
                        onClick={() => onDelete(p)}
                        disabled={busy}
                        data-testid={`ops-${kind}-delete-${p.id}`}
                        style={{ background: "#DC2626", color: "#fff", border: "none", padding: "4px 8px", borderRadius: 4, cursor: busy ? "wait" : "pointer", fontSize: 11 }}
                      >
                        Delete
                      </button>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
